import { Component, OnDestroy, OnInit, ViewChild, ElementRef, Output, EventEmitter, Input, signal } from '@angular/core';
import { TranslocoModule, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { DeviceSettingsService } from '../../library/device-settings.service';
import { WebRTCDiagnosticService } from 'src/app/services/general/webrtc-diagnostic.service';
import { IDiagnosticResult } from 'src/app/interfaces/idiagnostic-result';
import { TranslationService } from 'src/app/services/translation.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { RealtimeService } from '../../library/realtime/realtime.service';
import { QaiButtonComponent, QaiSpinnerComponent, QaiTooltipComponent, QaiFloatingDirective } from '@qai/ui';

@Component({
  selector: 'app-device-settings-diagnostic',
  templateUrl: './device-settings-diagnostic.component.html',
  styleUrls: ['./device-settings-diagnostic.component.scss'],
  standalone: true,
  imports: [
    TranslocoModule,
    QaiButtonComponent,
    QaiSpinnerComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'settings',
    },
  ],
})
export class DeviceSettingsDiagnosticComponent implements OnInit, OnDestroy {
  @Input() deviceSettingsService?: DeviceSettingsService;
  @Output() diagnosticRunningChange = new EventEmitter<boolean>();
  @ViewChild('floatingRef', { static: false }) floatingRef?: ElementRef;

  readonly isRunningDiagnostic = signal(false);
  readonly isDiagnosticComplete = signal(false);
  readonly diagnosticResult = signal<IDiagnosticResult | null>(null);

  // Declared in TS to ease future localization effort
  problemsConnectingPrefix = '';
  clickHereLabel = '';
  toRunDiagnosticSuffix = '';

  constructor(
    private rtcDiagnosticService: WebRTCDiagnosticService,
    private realtimeService: RealtimeService,
    private translationService: TranslationService,
    private logger: LoggerService
  ) { }

  ngOnInit(): void {
    this.resetDiagnosticState();
    this.problemsConnectingPrefix = this.translationService.translate('DIAGNOSTIC_PROBLEMS_CONNECTING_PREFIX', 'settings');
    this.clickHereLabel = this.translationService.translate('DIAGNOSTIC_CLICK_HERE', 'settings');
    this.toRunDiagnosticSuffix = this.translationService.translate('DIAGNOSTIC_TO_RUN_SUFFIX', 'settings');
  }

  ngOnDestroy(): void {
    this.resetDiagnosticState();
  }

  private resetDiagnosticState(): void {
    this.isRunningDiagnostic.set(false);
    this.isDiagnosticComplete.set(false);
    this.diagnosticResult.set(null);
    this.diagnosticRunningChange.emit(false);
    if (this.deviceSettingsService) this.deviceSettingsService.isDiagnosticRunning = false;
    this.logger.log('[CALL] Diagnostic State - Cleaned');
  }

  async runDiagnostic(auto = false) {
    if (this.isRunningDiagnostic()) return;
    this.isRunningDiagnostic.set(true);
    this.diagnosticRunningChange.emit(true);
    if (this.deviceSettingsService) this.deviceSettingsService.isDiagnosticRunning = true;
    try {
      const activeCallObject = this.realtimeService.dailyCall;
      const isCallJoined = this.realtimeService.joinedMeeting;
      const result = await this.rtcDiagnosticService.runDiagnosticsAuto(activeCallObject, isCallJoined);
      this.diagnosticResult.set(result);
      this.logger.log('[CALL] Diagnostic Result', result);
    } catch (err) {
      this.diagnosticResult.set({
        summary: this.translationService.translate('DIAGNOSTIC_FAILED', 'settings'),
        timestamp: new Date().toISOString(),
        raw: { error: String(err) },
        hasPassed: false,
      } as any);
      this.logger.error('[CALL] Diagnostic Result', err, this.diagnosticResult() || {});
    } finally {
      this.isRunningDiagnostic.set(false);
      this.isDiagnosticComplete.set(true);
      this.diagnosticRunningChange.emit(false);
      if (this.deviceSettingsService) this.deviceSettingsService.isDiagnosticRunning = false;
    }
  }

  async onRunDiagnosticClick() {
    if (this.diagnosticResult()) {
      this.resetDiagnosticState();
    }
    await this.runDiagnostic();
  }

  onCopyResultsClick() {
    this.copyResultsToClipboard();
  }

  copyResultsToClipboard() {
    const result = this.diagnosticResult();
    if (!result) return;
    const payload = this.formatPayloadForSupport(result);
    navigator.clipboard.writeText(payload);
  }

  private formatPayloadForSupport(res: IDiagnosticResult) {
    const header = `${this.translationService.translate('DIAGNOSTIC_QUANTIFIED_DIAGNOSTICS', 'settings')}\n${this.translationService.translate('DIAGNOSTIC_TIME', 'settings')}: ${res.timestamp}\n${this.translationService.translate('DIAGNOSTIC_SUMMARY', 'settings')}: ${res.summary}\n\n`;
    const raw = JSON.stringify(res.raw, null, 2);
    const meta = `\n\n${this.translationService.translate('DIAGNOSTIC_CLIENT_INFO', 'settings')}:\n- UA: ${navigator.userAgent}\n- ${this.translationService.translate('DIAGNOSTIC_PLATFORM', 'settings')}: ${navigator.platform}\n`;
    return header + raw + meta;
  }
}

